import React from 'react';
import { Download, Users, Mail, MapPin, Printer, Copy, CheckSquare, Square, Minus } from 'lucide-react';
import ThemedCard from '../../themed/ThemedCard';
import { RSVPResponseWithTable, PhysicalInvitationStats } from '../types';

interface PhysicalInvitationsTabProps {
  physicalInvitationStats: PhysicalInvitationStats;
  rsvpResponses: RSVPResponseWithTable[];
  exportingData: boolean;
  selectedPhysicalInvitations: Set<number>;
  copiedIndex: number | null;
  togglePhysicalInvitationSelection: (index: number) => void;
  selectAllPhysicalInvitations: () => void;
  getPhysicalInvitationSelectAllState: () => 'none' | 'all' | 'partial';
  copyToClipboard: (text: string, index: number) => void;
  exportPhysicalInvitationsToCSV: (includePhone?: boolean) => void;
  setShowExportOptions: (v: boolean) => void;
}

export const PhysicalInvitationsTab: React.FC<PhysicalInvitationsTabProps> = ({
  physicalInvitationStats,
  rsvpResponses,
  exportingData,
  selectedPhysicalInvitations,
  copiedIndex,
  togglePhysicalInvitationSelection,
  selectAllPhysicalInvitations,
  getPhysicalInvitationSelectAllState,
  copyToClipboard,
  exportPhysicalInvitationsToCSV,
  setShowExportOptions,
}) => {
  return (
    <ThemedCard className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-wedding-text-title mb-2">實體喜帖寄送管理</h3>
          <p className="text-wedding-text-body">管理需要寄送實體喜帖的賓客名單與地址資訊</p>
        </div>
        <div className="flex items-center space-x-2">
          {selectedPhysicalInvitations.size > 0 && (
            <span className="text-sm text-wedding-text-body">
              已選擇 <span className="font-semibold text-wedding-pink">{selectedPhysicalInvitations.size}</span> 項
            </span>
          )}
          <button
            onClick={() => exportPhysicalInvitationsToCSV(false)}
            disabled={exportingData || physicalInvitationStats.addressList.length === 0}
            className="flex items-center px-4 py-2 bg-wedding-gold hover:bg-wedding-gold/90 disabled:bg-gray-400 text-white rounded-md transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            {exportingData ? '匯出中...' : selectedPhysicalInvitations.size > 0 ? `匯出 CSV (${selectedPhysicalInvitations.size})` : '匯出 CSV'}
          </button>
          <button
            onClick={() => setShowExportOptions(true)}
            disabled={exportingData || physicalInvitationStats.addressList.length === 0 || selectedPhysicalInvitations.size === 0}
            className="flex items-center px-4 py-2 bg-wedding-pink hover:bg-wedding-pink/90 disabled:bg-gray-400 text-white rounded-md transition-colors"
          >
            <Printer className="h-4 w-4 mr-2" />
            {selectedPhysicalInvitations.size > 0 ? `匯出郵寄 (${selectedPhysicalInvitations.size})` : '匯出郵寄'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <ThemedCard className="p-4">
          <div className="flex items-center">
            <Mail className="h-8 w-8 text-wedding-pink mr-3" />
            <div>
              <p className="text-sm font-medium text-wedding-text-body">需要實體喜帖</p>
              <p className="text-2xl font-bold text-wedding-pink">{physicalInvitationStats.totalRequests}</p>
              <p className="text-xs text-wedding-text-body">位賓客</p>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard className="p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-wedding-gold mr-3" />
            <div>
              <p className="text-sm font-medium text-wedding-text-body">總出席人數</p>
              <p className="text-2xl font-bold text-wedding-gold">{physicalInvitationStats.totalGuests}</p>
              <p className="text-xs text-wedding-text-body">人</p>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard className="p-4">
          <div className="flex items-center">
            <MapPin className="h-8 w-8 text-teal-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-wedding-text-body">已整理地址</p>
              <p className="text-2xl font-bold text-teal-600">{physicalInvitationStats.addressList.length}</p>
              <p className="text-xs text-wedding-text-body">筆</p>
            </div>
          </div>
        </ThemedCard>
      </div>

      {physicalInvitationStats.addressList.length === 0 ? (
        <div className="text-center py-12">
          <Mail className="h-12 w-12 text-wedding-text-body mx-auto mb-4" />
          <h3 className="text-lg font-medium text-wedding-text-title mb-2">目前沒有賓客申請實體喜帖</h3>
          <p className="text-wedding-text-body">當賓客在 RSVP 表單中勾選需要實體喜帖時，會顯示在這裡</p>
        </div>
      ) : (
        <ThemedCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-wedding-pink/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <button
                      onClick={selectAllPhysicalInvitations}
                      className="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-wedding-pink"
                      title={getPhysicalInvitationSelectAllState() === 'all' ? '取消全選' : '全選'}
                    >
                      {(() => {
                        const selectState = getPhysicalInvitationSelectAllState();
                        if (selectState === 'all') return <CheckSquare className="h-4 w-4 text-wedding-pink" />;
                        if (selectState === 'partial') return <Minus className="h-4 w-4 text-wedding-pink" />;
                        return <Square className="h-4 w-4" />;
                      })()}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    賓客姓名
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    出席人數
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    郵寄地址
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    電話號碼
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {physicalInvitationStats.addressList.map((request, index) => (
                  <tr key={index} className={`hover:bg-gray-50 ${selectedPhysicalInvitations.has(index) ? 'bg-wedding-pink/5' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => togglePhysicalInvitationSelection(index)}
                        className="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-wedding-pink"
                      >
                        {selectedPhysicalInvitations.has(index) ? (
                          <CheckSquare className="h-4 w-4 text-wedding-pink" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {request.guestName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm font-medium text-gray-900">
                          {request.guestCount} 人
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs">
                        {request.postalAddress}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(() => {
                          const matchingRSVP = rsvpResponses.find(rsvp =>
                            rsvp.guestName === request.guestName &&
                            rsvp.rsvpData.attending === 'yes' &&
                            rsvp.rsvpData.needsPhysicalInvitation
                          );
                          return matchingRSVP?.rsvpData.contactPhone || '-';
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => copyToClipboard(request.postalAddress, index)}
                        className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                      >
                        {copiedIndex === index ? (
                          <>
                            <CheckSquare className="h-4 w-4 text-green-500 mr-1" />
                            已複製
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1" />
                            複製地址
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ThemedCard>
      )}

      {physicalInvitationStats.totalRequests > 0 && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2">寄送說明</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• 所有地址資訊由賓客在 RSVP 表單中自行填寫</li>
            <li>• 建議在寄送前再次確認地址的完整性和正確性</li>
            <li>• 可使用「複製地址」功能快速複製地址到其他應用程式</li>
            <li>• 點擊「匯出 CSV」可下載完整的寄送清單供列印使用</li>
          </ul>
        </div>
      )}
    </ThemedCard>
  );
};
